import type { NavigationGroupDto } from '@an-crm/shared';
import { Link, useRouterState } from '@tanstack/react-router';
import { ChevronRight, House } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { Fragment } from 'react';
import { useCrumbTail } from './crumbs';
import { buildCrumbs } from './navigation';

export function Breadcrumbs({ nav }: { nav: NavigationGroupDto[] }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const tail = useCrumbTail();
  const crumbs = buildCrumbs(nav, pathname, tail);

  return (
    <nav aria-label="Breadcrumb" className="min-w-0">
      <ol className="flex min-w-0 items-center gap-1.5 text-sm">
        <li className="flex shrink-0">
          <Link
            to="/"
            className="focus-ring flex items-center rounded p-0.5 text-fg-subtle transition-colors hover:text-fg"
            aria-label="Home"
            aria-current={crumbs.length === 0 ? 'page' : undefined}
          >
            <House className="size-4" />
          </Link>
        </li>
        <AnimatePresence initial={false} mode="popLayout">
          {crumbs.map((c, i) => {
            const last = i === crumbs.length - 1;
            return (
              <Fragment key={`${i}-${c.label}`}>
                <motion.li
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18, delay: i * 0.03 }}
                  className="flex min-w-0 items-center gap-1.5"
                >
                  <ChevronRight className="size-3.5 shrink-0 text-fg-subtle/70" aria-hidden />
                  {c.to && !last ? (
                    <Link
                      to={c.to}
                      className="focus-ring truncate rounded text-fg-muted transition-colors hover:text-fg"
                    >
                      {c.label}
                    </Link>
                  ) : (
                    <span
                      className={last ? 'truncate font-medium text-fg' : 'truncate text-fg-muted'}
                      aria-current={last ? 'page' : undefined}
                    >
                      {c.label}
                    </span>
                  )}
                </motion.li>
              </Fragment>
            );
          })}
        </AnimatePresence>
      </ol>
    </nav>
  );
}
